import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[32]}px;
  padding: ${sizes.spacing[40]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
    /* EGDE-274: the theme draws the divider on .MuiTabs-flexContainer
       (width:fit-content, needed for scrolling), so in RTL it hugged the tabs
       and stopped short of the strip ends / scroll-button chevrons. Keep that
       border (the active tab's orange underline overlaps it via a -2px margin
       and must not be clipped) and add a full-width rule BEHIND the strip so the
       gray line reaches end to end, under the chevrons. */
    position: relative;
    isolation: isolate;

    &::after {
      content: "";
      position: absolute;
      inset-inline: 0;
      bottom: 0;
      height: 2px;
      background: ${colors.primitives.neutral300};
      z-index: -1;
    }
  }

  /* EGDE-274: MUI's default Tab min-width (72–90px) padded short category
     labels into wide, uneven gaps on the career page. Let each tab hug its
     label so only the flexContainer gap (16px) separates them — same fix as
     the magazine lobby. */
  .MuiTab-root {
    min-width: auto;
  }
`

/* ------------------------------------------------------------------ */
/* Inner Hero — title + subtitle + tabs                                */
/* ------------------------------------------------------------------ */
export const InnerHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${sizes.spacing[32]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[40]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Text Container — title + subtitle                                   */
/* ------------------------------------------------------------------ */
export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Title row — title text + chevron decoration                         */
/* RTL: source [Title, Chevron] => visual [Chevron LEFT, Title RIGHT]  */
/* ------------------------------------------------------------------ */
export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const PageTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-size: 32px !important;
  line-height: 38px !important;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 56px !important;
    line-height: 64px !important;
  }
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 38px;

  svg {
    width: 16px;
    height: 26px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    height: 64px;

    svg {
      width: 19px;
      height: 32px;
    }
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const PageSubtitle = styled(Typography)`
  color: ${colors.light.text.primary};
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Filter area — region chips                                          */
/* ------------------------------------------------------------------ */
export const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

export const FilterLabel = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
`

export const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

export const Chip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[8]}px ${sizes.spacing[16]}px;
  border-radius: ${sizes.borderRadius.full}px;
  border: ${(p) =>
    p.$active ? "none" : `1px solid ${colors.primitives.neutral300}`};
  background: ${(p) =>
    p.$active ? colors.gradients.gradientCta : colors.light.bg.primary};
  cursor: pointer;
  transition: background 200ms ease-in-out, border 200ms ease-in-out;
  white-space: nowrap;

  /* EGDE-496: inactive region chips get the subtle hover ring; the active
     gradient chip is already "on" and stays put. */
  &:hover {
    ${(p) =>
      p.$active
        ? ""
        : `border-color: ${colors.primitives.neutral500};
    background: ${colors.primitives.neutral100};`}
  }
`

export const ChipText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  color: ${colors.primitives.neutral600};
`

/* ------------------------------------------------------------------ */
/* Total counter                                                       */
/* ------------------------------------------------------------------ */
export const TotalCounter = styled(Typography)`
  color: ${colors.primitives.neutral600};
  width: 100%;
  text-align: start;
`

export const BoldCount = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 22px;
`

/* ------------------------------------------------------------------ */
/* Card grid                                                           */
/* ------------------------------------------------------------------ */
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.spacing[16]}px;
  width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${sizes.spacing[24]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Job card                                                            */
/* ------------------------------------------------------------------ */
export const JobCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding: ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius[24]}px;
  background-color: ${colors.light.bg.primary};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 150ms ease-in-out;

  &:hover {
    border-color: ${colors.primitives.neutral500};
  }
`

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  flex: 1;
`

export const CardHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const CardTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

export const CardExcerpt = styled(Typography)`
  color: ${colors.light.text.primary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* ------------------------------------------------------------------ */
/* Card footer — location + CTA                                       */
/* Figma visual L→R: [CTA LEFT, location RIGHT]                       */
/* RTL: source [location, CTA] => visual [CTA LEFT, location RIGHT]   */
/* ------------------------------------------------------------------ */
export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* CTA link                                                            */
/* Figma visual L→R: [arrow LEFT, text RIGHT]                          */
/* RTL: source [text, arrow] => visual [arrow LEFT, text RIGHT]        */
/* ------------------------------------------------------------------ */
export const CtaLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
`

export const CtaText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  white-space: nowrap;
`

export const CtaArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.primitives.primary500};

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* Location row                                                        */
/* Figma visual L→R: [icon LEFT, text RIGHT]                           */
/* RTL: source [text, icon] => visual [icon LEFT, text RIGHT]          */
/* ------------------------------------------------------------------ */
export const LocationRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
`

export const LocationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: ${colors.light.text.primary};

  [dir="ltr"] & {
    order: -1;
  }
`

export const LocationText = styled(Typography)`
  color: ${colors.light.text.primary};
  white-space: nowrap;
`

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

export const PageButton = styled.button<{ $active: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? colors.primitives.warning500
        : colors.primitives.neutral300};
  background-color: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out,
    color 150ms ease-in-out;

  &:hover:not(:disabled) {
    border-color: ${colors.primitives.neutral500};
  }
`

export const PageButtonText = styled.span<{ $active: boolean }>`
  font-size: ${(p) => (p.$active ? 20 : 16)}px;
  font-weight: 500;
  line-height: 1;
  color: ${(p) =>
    p.$active ? colors.primitives.neutral600 : colors.primitives.neutral500};
`

export const Ellipsis = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 16px;
  color: ${colors.primitives.neutral500};
  user-select: none;
`

export const PaginationArrow = styled.button`
  /* EGDE-496 r2: 32px hit area matches the page circles, so the hover disc
     reads at the same size as the FAQ pagination (a 20px disc was invisible /
     "too small" per QA). */
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.primitives.neutral600};
  transition: background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  /* EGDE-496 r2: the careers arrows were the one pagination without a hover
     state — same neutral200 disc as every other pagination arrow. */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral200};
  }
`
